import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { ANDROID_PLATFORM, IOS_PLATFORM } from '../../constants';
import { getPlatformFamilyName, isIosOrAndroid, isNonGamingPlatform } from '../../utils';
import { SwitchLine } from '../controls';
import css from './AdvancedSettings.css';
import AdMonSection from './AdMonSection';
import AndroidLinksSection from './AndroidLinksSection';
import AppClipsSection from './AppClipsSection';
import AttributionSection from './AttributionSection';
import DestinationsSection from './DestinationsSection';
import IOSDeepLinksSection from './IOSDeepLinksSection';
import UninstallTrackingSection from './UninstallTrackingSection';
import UserPrivacySection from './UserPrivacySection';
import { admonSourceListPropType, sitePropType, validationErrorsPropType } from './propTypes';

const canShowAdvancedSettingsToggle = ({ site, configuration, userData, appsPermissions }) => {
    const platformName = getPlatformFamilyName(site);
    const apsalarEnabled = !!configuration.apsalar_enabled;
    const hasSdkProduct = !!(userData?.attribution_enabled || userData?.sdk_enabled);
    const gamingAdvancedPlatforms = ['metaquest', 'nintendo', 'pc', 'ctv', 'xbox', 'playstation'];
    const platformSupportsAdvanced =
        isNonGamingPlatform(site) || (gamingAdvancedPlatforms.includes(platformName) && !!site.sdk_integrated);

    return (
        !!site.id &&
        platformSupportsAdvanced &&
        hasSdkProduct &&
        (!!appsPermissions.userPrivacyEnabled || apsalarEnabled)
    );
};

const AdvancedSettings = ({
    site,
    appId,
    appName,
    configuration,
    appsPermissions,
    adminMode,
    translate,
    userData,
    readOnly,
    onSitePatch,
    onTestFcmIntegration,
    onTrackAppsEvent,
    validationErrors = {},
}) => {
    const platformFamilyName = getPlatformFamilyName(site);
    const apsalarEnabled = !!configuration.apsalar_enabled;
    const singularLinksEnabled = !!configuration.singular_links_enabled;
    const zeroInactivityWindowSupported = !!configuration.zero_inactivity_window_supported;
    const userPrivacyEnabled = !!appsPermissions.userPrivacyEnabled;
    const hasSdkProduct = !!(userData?.attribution_enabled || userData?.sdk_enabled);
    const appClipsEnabled = !!(userData?.app_clips_enabled || userData?.appClipsEnabled);
    const admonConfig = configuration.admon_config || {};
    const siteAllowsAdvanced =
        !!site.allow_show_advanced_settings ||
        (apsalarEnabled &&
            !!site.sdk_integrated &&
            (isIosOrAndroid(site) || (site._published === false && !!site.platform)) &&
            !readOnly);
    const showAdmon =
        siteAllowsAdvanced && (adminMode || !!admonConfig.enable_admon_apps_ui) && isNonGamingPlatform(site);
    const showAttribution = !!site.sdk_integrated && hasSdkProduct;
    const showAdvancedToggle = canShowAdvancedSettingsToggle({ site, configuration, userData, appsPermissions });
    const updateDestinations = (destinationType, destinations) => {
        onSitePatch({
            destinations: {
                regular: site.destinations?.regular || [],
                deeplink: site.destinations?.deeplink || [],
                ios_universal_link: site.destinations?.ios_universal_link || [],
                [destinationType]: destinations,
            },
        });
    };
    const trackDestinationChange = (destinationType, nextDestinations) => {
        const currentDestinations = site.destinations?.[destinationType] || [];

        if (nextDestinations.length > currentDestinations.length) {
            if (destinationType === 'regular') {
                onTrackAppsEvent('add destination', {
                    Destination: nextDestinations[nextDestinations.length - 1]?.url || '',
                });
            }
            if (destinationType === 'deeplink') {
                onTrackAppsEvent('add deep link', {
                    deeplink: nextDestinations[nextDestinations.length - 1]?.url || '',
                });
            }
        }

        if (nextDestinations.length < currentDestinations.length) {
            const removedDestination =
                currentDestinations.find(
                    currentDestination =>
                        !nextDestinations.some(
                            nextDestination =>
                                nextDestination.id === currentDestination.id &&
                                nextDestination.url === currentDestination.url
                        )
                ) || currentDestinations[currentDestinations.length - 1];

            if (destinationType === 'regular') {
                onTrackAppsEvent('remove destination', { destination: removedDestination });
            }
            if (destinationType === 'deeplink') {
                onTrackAppsEvent('remove deep link', { deeplink: removedDestination });
            }
        }
    };
    const handleDestinationChange = (destinationType, destinations) => {
        trackDestinationChange(destinationType, destinations);
        updateDestinations(destinationType, destinations);
    };

    if (!showAdvancedToggle) {
        return null;
    }

    return (
        <div className={css.advancedArea}>
            <SwitchLine
                checked={!!site.show_advanced_settings}
                onChange={checked => onSitePatch({ show_advanced_settings: checked })}
                withRule
                label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.SHOW_ADVANCED_SETTINGS" />}
            />
            {site.show_advanced_settings && (
                <div className={css.advancedContent}>
                    {showAdmon && (
                        <AdMonSection
                            site={site}
                            appId={appId}
                            appName={appName}
                            admonConfig={admonConfig}
                            adminMode={adminMode}
                            readOnly={readOnly}
                            translate={translate}
                            onSitePatch={onSitePatch}
                            onTrackAppsEvent={onTrackAppsEvent}
                        />
                    )}

                    {siteAllowsAdvanced && isIosOrAndroid(site) && (
                        <UninstallTrackingSection
                            site={site}
                            platformFamilyName={platformFamilyName}
                            apsalarEnabled={apsalarEnabled}
                            readOnly={readOnly}
                            configuration={configuration}
                            translate={translate}
                            validationErrors={validationErrors}
                            onSitePatch={onSitePatch}
                            onTestFcmIntegration={onTestFcmIntegration}
                        />
                    )}

                    {apsalarEnabled && platformFamilyName === IOS_PLATFORM && appClipsEnabled && (
                        <AppClipsSection site={site} readOnly={readOnly} onSitePatch={onSitePatch} />
                    )}

                    {apsalarEnabled && platformFamilyName === IOS_PLATFORM && (
                        <IOSDeepLinksSection
                            site={site}
                            singularLinksEnabled={singularLinksEnabled}
                            readOnly={readOnly}
                            translate={translate}
                            validationErrors={validationErrors}
                            onSitePatch={onSitePatch}
                        />
                    )}

                    {apsalarEnabled && singularLinksEnabled && platformFamilyName === ANDROID_PLATFORM && (
                        <AndroidLinksSection
                            site={site}
                            translate={translate}
                            readOnly={readOnly}
                            onSitePatch={onSitePatch}
                        />
                    )}

                    {isNonGamingPlatform(site) && (
                        <DestinationsSection
                            site={site}
                            platformFamilyName={platformFamilyName}
                            apsalarEnabled={apsalarEnabled}
                            singularLinksEnabled={singularLinksEnabled}
                            readOnly={readOnly}
                            translate={translate}
                            onDestinationChange={handleDestinationChange}
                        />
                    )}

                    {userPrivacyEnabled && isNonGamingPlatform(site) && (
                        <UserPrivacySection site={site} readOnly={readOnly} onSitePatch={onSitePatch} />
                    )}

                    {showAttribution && (
                        <AttributionSection
                            site={site}
                            readOnly={readOnly}
                            zeroInactivityWindowSupported={zeroInactivityWindowSupported}
                            translate={translate}
                            validationErrors={validationErrors}
                            onSitePatch={onSitePatch}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

AdvancedSettings.propTypes = {
    adminMode: PropTypes.bool.isRequired,
    appId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    appName: PropTypes.string.isRequired,
    appsPermissions: PropTypes.shape({
        appKeywordEnabled: PropTypes.bool,
        fraudPermitted: PropTypes.bool,
        userPrivacyEnabled: PropTypes.bool,
    }).isRequired,
    configuration: PropTypes.shape({
        admon_config: PropTypes.shape({
            admon_revenue_sources_list: admonSourceListPropType,
            enable_admon_apps_ui: PropTypes.bool,
        }),
        apsalar_enabled: PropTypes.bool,
        apps_fcm_project_id_readonly: PropTypes.bool,
        singular_links_enabled: PropTypes.bool,
        zero_inactivity_window_supported: PropTypes.bool,
    }).isRequired,
    onSitePatch: PropTypes.func.isRequired,
    onTestFcmIntegration: PropTypes.func.isRequired,
    onTrackAppsEvent: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    translate: PropTypes.func.isRequired,
    userData: PropTypes.shape({
        appClipsEnabled: PropTypes.bool,
        app_clips_enabled: PropTypes.bool,
        attribution_enabled: PropTypes.bool,
        sdk_enabled: PropTypes.bool,
    }).isRequired,
    validationErrors: validationErrorsPropType,
};

export default AdvancedSettings;
